class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this._boundHandleDocumentKeyDown = this._handleDocumentKeyDown.bind(this);
  }

  setHeaderCartIconAccessibility() {
    // Bind every cart trigger (desktop, mobile, nav-drawer, menu-drawer) so the
    // drawer opens from any cart icon — they all share the header__icon--cart class.
    const cartLinks = document.querySelectorAll('a.header__icon--cart');
    if (!cartLinks.length) return;
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 30);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (typeof trapFocus === 'function') {
            trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    window.ScrollLock && ScrollLock.lock('cart-drawer');
    document.addEventListener('keydown', this._boundHandleDocumentKeyDown);
  }

  close() {
    if (!this.classList.contains('active')) return;

    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function' && this.activeElement) {
        removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
    window.ScrollLock && ScrollLock.unlock('cart-drawer');
    document.removeEventListener('keydown', this._boundHandleDocumentKeyDown);
  }

  _handleDocumentKeyDown(event) {
    if (event.code === 'Escape') {
      if (this.classList.contains('active')) {
        const isModalDrawerOpen = document.querySelector('modal-drawer[open]');
        if (isModalDrawerOpen) {
          return;
        }
        event.preventDefault();
        this.close();
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (typeof onKeyUpEscape === 'function') {
        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
        drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
          overlay.removeEventListener('click', this.close.bind(this));
          overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    }, 50);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ---- In-place DOM morph ----
   Patches the live drawer against fresh server HTML instead of replacing
   innerHTML wholesale. Unchanged nodes (notably <img> and the focused
   stepper button) are kept, so a quantity change no longer flashes/reloads
   the whole panel. */
const morphAttributes = (fromEl, toEl) => {
  for (const attr of Array.from(fromEl.attributes)) {
    if (!toEl.hasAttribute(attr.name)) fromEl.removeAttribute(attr.name);
  }
  for (const attr of Array.from(toEl.attributes)) {
    if (fromEl.getAttribute(attr.name) !== attr.value) fromEl.setAttribute(attr.name, attr.value);
  }
  // Keep the displayed input value in sync with the server-confirmed value.
  if (fromEl.tagName === 'INPUT' && toEl.hasAttribute('value')) {
    fromEl.value = toEl.getAttribute('value');
  }
};

const morphNode = (fromNode, toNode) => {
  const differentType =
    fromNode.nodeType !== toNode.nodeType ||
    (fromNode.nodeType === Node.ELEMENT_NODE && fromNode.tagName !== toNode.tagName);

  if (differentType) {
    fromNode.replaceWith(toNode.cloneNode(true));
    return;
  }

  if (fromNode.nodeType === Node.TEXT_NODE || fromNode.nodeType === Node.COMMENT_NODE) {
    if (fromNode.nodeValue !== toNode.nodeValue) fromNode.nodeValue = toNode.nodeValue;
    return;
  }

  if (fromNode.nodeType !== Node.ELEMENT_NODE) return;

  morphAttributes(fromNode, toNode);

  // Leave a matching image untouched so it never re-decodes / re-fades.
  if (fromNode.tagName === 'IMG' && fromNode.getAttribute('src') === toNode.getAttribute('src')) {
    return;
  }

  const fromChildren = Array.from(fromNode.childNodes);
  const toChildren = Array.from(toNode.childNodes);
  const max = Math.max(fromChildren.length, toChildren.length);
  for (let i = 0; i < max; i++) {
    const f = fromChildren[i];
    const t = toChildren[i];
    if (f && t) {
      morphNode(f, t);
    } else if (t) {
      fromNode.appendChild(t.cloneNode(true));
    } else if (f) {
      fromNode.removeChild(f);
    }
  }
};

/* window.CartItems, not the bare identifier: assets/cart.js wraps itself in a
   re-entry guard block, so its `class CartItems` is block-scoped and invisible
   here. See the note beside the export in cart.js (AAS-5514). */
class CartDrawerItems extends window.CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    // Morph the drawer panel in place rather than returning HTML for a
    // destructive `innerHTML =` assignment. Returning null makes the caller
    // (CartItems.updateQuantity) skip the swap via its `if (newInner)` guard.
    if (selector === '.drawer__inner') {
      const liveEl = document.querySelector('#CartDrawer .drawer__inner');
      const newEl = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (liveEl && newEl) {
        // Preserve the scroll position so changing the quantity of a lower
        // item doesn't jump the list back to the top. The jump comes from the
        // focus() the caller runs after this returns (and any morph height
        // clamp), which can land a frame later — so re-assert the position for
        // a short window, re-querying the scroller in case it was replaced.
        const scrollerEl = document.querySelector('cart-drawer-items');
        const savedScroll = scrollerEl ? scrollerEl.scrollTop : 0;
        morphNode(liveEl, newEl);
        if (scrollerEl) {
          scrollerEl.scrollTop = savedScroll;
          const start = performance.now();
          const keepScroll = () => {
            const s = document.querySelector('cart-drawer-items');
            if (s && Math.abs(s.scrollTop - savedScroll) > 1) {
              s.scrollTop = savedScroll;
            }
            if (performance.now() - start < 400) requestAnimationFrame(keepScroll);
          };
          requestAnimationFrame(keepScroll);
        }
        return null;
      }
    }
    return super.getSectionInnerHTML(html, selector);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* AAS-5514 — reconcile the drawer with the real cart when a page is shown.

   The bug: add to cart, press Back, open the cart — the count is right but the
   drawer is empty (or shows the previous contents). Chrome answers Back from a
   cache, so the drawer markup is whatever Liquid rendered before the add, and
   nothing corrects it: CartDrawer.open() only animates and traps focus, and
   cart-count-sync.js owns the badge but not this.

   WHY NOT GATE ON THE NAVIGATION TYPE
   The obvious guard — refresh only when performance navigation type is
   "back_forward", or pageshow.persisted is true — does not hold. Measured on
   qual, mobile viewport, homepage -> search -> PDP -> add -> Back: the restored
   search page reported type "navigate" with a non-zero transferSize while its
   drawer was demonstrably stale (server HTML for the same URL had the item, the
   live DOM did not). Desktop reported "back_forward" for the same flow, which is
   why this looked desktop-only. The signal cannot be trusted to decide.

   So decide on the DATA instead: ask the cart what it holds and only touch the
   DOM when the two disagree. /cart.js is small and uncached; a matching count
   costs one request per page show and no section render. Nothing is rewritten
   in the common case, which also means no focus or scroll disturbance.

   Why not reuse renderContents(): it ends by calling open(), which would pop the
   drawer open on every page show. This is the same section swap without that. */
(() => {
  let inFlight = false;

  /* Compare TOTAL UNITS, not line count. A line count alone misses the case
     where the shopper re-adds the same variant: the cart still has one line, so
     nothing looks different, while the rendered quantity is a step behind.
     Summing the quantity inputs the drawer already renders catches both a new
     line and a quantity bump, and needs no extra request. */
  const renderedUnits = (drawer) =>
    [...drawer.querySelectorAll('.cart-item')].reduce((total, item) => {
      const input = item.querySelector('input[name="updates[]"]');
      const qty = input ? parseInt(input.value, 10) : NaN;
      return total + (Number.isNaN(qty) ? 1 : qty);
    }, 0);

  const reconcile = async () => {
    const drawer = document.querySelector('cart-drawer');
    const target = document.getElementById('CartDrawer');
    if (!drawer || !target || inFlight) return;
    /* Deliberately NOT skipped when the drawer is open.
       An earlier version bailed out on `.active`, reasoning that a shopper is
       looking at it and a swap would move focus. That is wrong on a bfcache
       restore: the DOM comes back exactly as it was left, so a drawer the
       shopper opened BEFORE navigating away is restored already open, holding
       the contents from before the add — the one case that most needs
       refreshing is the one that guard skipped.

       Reproduced: open the drawer, follow a product link out of it, add a
       different variant on the PDP, press Back. The drawer returns open and
       missing the item that was just added, while the badge is correct.

       Swapping under a genuinely open drawer is the lesser evil: the contents
       are wrong, and the alternative is showing the shopper a cart that does
       not match what they just did. The swap replaces #CartDrawer's children
       only, so the host keeps its `active` class and the drawer stays open. */

    inFlight = true;
    try {
      const root = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : '/cart';
      const cart = await fetch(`${root}.js`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (!cart || typeof cart.item_count !== 'number') return;

      if (cart.item_count === renderedUnits(drawer)) return;

      const base = typeof getSectionsUrl === 'function' ? getSectionsUrl() : window.location.pathname;
      const url = `${base}${base.includes('?') ? '&' : '?'}section_id=cart-drawer`;
      /* cache: 'no-store' is load-bearing — the reason this runs is that the
         browser answered from its disk cache, and the section request is served
         from that same cache unless told otherwise, handing back the very markup
         being replaced. */
      const html = await fetch(url, { cache: 'no-store' })
        .then((r) => (r.ok ? r.text() : null))
        .catch(() => null);
      if (!html) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const freshInner = fresh.getElementById('CartDrawer');
      if (!freshInner) return;
      target.innerHTML = freshInner.innerHTML;

      /* `is-empty` is rendered onto BOTH <cart-drawer> and <cart-drawer-items>
         (snippets/cart-drawer.liquid lines 11 and 83). Only the second sits
         inside #CartDrawer and came across in the swap above, so the host class
         is mirrored by hand or open() reads the wrong branch when choosing what
         to trap focus on. */
      const freshItems = fresh.querySelector('cart-drawer-items');
      if (freshItems) drawer.classList.toggle('is-empty', freshItems.classList.contains('is-empty'));
    } finally {
      inFlight = false;
    }
  };

  window.addEventListener('pageshow', reconcile);
})();
